/** Élément obligatoire : une erreur explicite vaut mieux qu'un `null` qui explose plus loin. */
export function $<T extends Element = HTMLElement>(selector: string, root: ParentNode = document): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Élément introuvable : ${selector}`);
  return element;
}

export function $$<T extends Element = HTMLElement>(selector: string, root: ParentNode = document): T[] {
  return [...root.querySelectorAll<T>(selector)];
}

type Props<K extends keyof HTMLElementTagNameMap> = Partial<Omit<HTMLElementTagNameMap[K], 'style' | 'dataset'>>;

/** Crée un élément ; le texte passe par des nœuds texte, jamais par innerHTML. */
export function el<K extends keyof HTMLElementTagNameMap>(tag: K, props: Props<K> = {}, ...children: (Node | string)[]): HTMLElementTagNameMap[K] {
  const element = Object.assign(document.createElement(tag), props);
  element.append(...children);
  return element;
}

/** Le pixel-art (petites images) est agrandi sans flou ; les illustrations HD restent lissées. */
export function enablePixelArtScaling(maxPixelArtSize = 256): void {
  document.addEventListener('load', (event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement) target.classList.toggle('pixel', target.naturalWidth <= maxPixelArtSize);
  }, true);
}

/** Force le redémarrage d'une animation CSS portée par une classe. */
/**
 * Lettre tapée (en minuscule) pour un raccourci clavier, selon la disposition du clavier (le M d'un clavier AZERTY est
 * bien « m ») ; null avec Ctrl, Cmd ou Alt, pour laisser au navigateur ses propres raccourcis.
 */
export function shortcutLetter(event: KeyboardEvent): string | null {
  if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) return null;
  return event.key.toLowerCase();
}

export function restartAnimation(element: Element, className: string): void {
  element.classList.remove(className);
  void (element as HTMLElement).offsetWidth;
  element.classList.add(className);
}

/** requestAnimationFrame est suspendu dans un onglet caché : on bascule alors sur un minuteur lent. */
export function scheduleFrame(callback: (now: number) => void, hiddenIntervalMs = 200): void {
  if (document.hidden) setTimeout(() => callback(performance.now()), hiddenIntervalMs);
  else requestAnimationFrame(callback);
}
